import React, { useState, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

// Componentes de App Health
import AppHealthOverview from './components/AppHealthOverview';
import DeploymentHistory from './components/DeploymentHistory';
import PerformanceMetrics from './components/PerformanceMetrics';
import LogsMonitor from './components/LogsMonitor';
import EnvironmentStatus from './components/EnvironmentStatus';
import BillingOverview from './components/BillingOverview';

// Servicios
import VercelService from '../../services/vercel/vercelService';
import billingService from '../../services/billing/billingService';

import {
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Server,
  Zap,
  Globe,
  Database,
  Shield,
  CreditCard
} from 'lucide-react';

/**
 * Página principal de App Health con integración Vercel + Supabase
 * Monitoreo completo de la salud de la aplicación
 */
const AppHealthPage = () => {
  const { session } = useSession();

  // Estados principales
  const [healthData, setHealthData] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [environmentStatus, setEnvironmentStatus] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [supabaseHealth, setSupabaseHealth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBillingLoading, setIsBillingLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Estados de navegación
  const [activeTab, setActiveTab] = useState('overview');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Servicio de Vercel
  const [vercelService, setVercelService] = useState(null);

  // Inicializar servicio
  useEffect(() => {
    if (session) {
      setVercelService(new VercelService(session));
    }
  }, [session]);

  // Cargar datos iniciales
  useEffect(() => {
    if (vercelService) {
      loadAppHealthData();
    }
    loadBillingData();
  }, [vercelService]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && vercelService) {
      const interval = setInterval(() => {
        loadAppHealthData(true);
      }, 30000);

      setRefreshInterval(interval);

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [autoRefresh, vercelService]);

  const loadAppHealthData = async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setIsLoading(true);
        setError(null);
      }

      const [
        healthStatus,
        deploymentsData,
        performanceData,
        logsData,
        envStatus
      ] = await Promise.all([
        vercelService.getAppHealth(),
        vercelService.getDeployments(),
        vercelService.getPerformanceMetrics(),
        vercelService.getLogs(),
        vercelService.getEnvironmentStatus()
      ]);

      setHealthData(healthStatus);
      setDeployments(deploymentsData);
      setPerformanceMetrics(performanceData);
      setLogs(logsData);
      setEnvironmentStatus(envStatus);
      setLastUpdate(new Date());

    } catch (err) {
      console.error('Error cargando datos de App Health:', err);
      setError('Error al cargar los datos de salud de la aplicación');
    } finally {
      if (!isAutoRefresh) {
        setIsLoading(false);
      }
    }
  };

  const loadBillingData = async () => {
    setIsBillingLoading(true);
    try {
      const [billing, health] = await Promise.allSettled([
        billingService.getBillingData(),
        billingService.getSupabaseHealth()
      ]);

      if (billing.status === 'fulfilled') setBillingData(billing.value);
      if (health.status === 'fulfilled') setSupabaseHealth(health.value);
    } catch (err) {
      console.error('Error cargando billing/health:', err);
    } finally {
      setIsBillingLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAppHealthData();
    loadBillingData();
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  // Obtener estado general de la aplicación
  const getOverallStatus = () => {
    if (!healthData) return { status: 'unknown', color: 'gray', icon: Activity };

    const { status } = healthData;

    switch (status) {
      case 'healthy':
        return { status: 'Saludable', color: 'green', icon: CheckCircle };
      case 'warning':
        return { status: 'Advertencia', color: 'yellow', icon: AlertCircle };
      case 'critical':
        return { status: 'Crítico', color: 'red', icon: AlertCircle };
      default:
        return { status: 'Desconocido', color: 'gray', icon: Activity };
    }
  };

  const overallStatus = getOverallStatus();
  const StatusIcon = overallStatus.icon || Activity;

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center space-x-3">
            <Activity className="h-8 w-8" />
            <span>App Health</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Monitoreo en tiempo real · Vercel + Supabase
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Estado general */}
          <Badge
            variant={overallStatus.color === 'green' ? 'default' :
                    overallStatus.color === 'yellow' ? 'secondary' : 'destructive'}
            className="flex items-center space-x-1 px-3 py-1"
          >
            <StatusIcon className="h-4 w-4" />
            <span>{overallStatus.status}</span>
          </Badge>

          {/* Controles */}
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAutoRefresh}
            className={autoRefresh ? 'bg-green-50 border-green-200' : ''}
          >
            <Zap className={`h-4 w-4 mr-2 ${autoRefresh ? 'text-[var(--yool-success)]' : ''}`} />
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Última actualización */}
      {lastUpdate && (
        <div className="flex items-center text-sm text-muted-foreground">
          <Clock className="h-4 w-4 mr-1" />
          Última actualización: {lastUpdate.toLocaleString()}
        </div>
      )}

      {/* Tabs principales */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview" className="flex items-center space-x-2">
            <Activity className="h-4 w-4" />
            <span>Overview</span>
          </TabsTrigger>
          <TabsTrigger value="deployments" className="flex items-center space-x-2">
            <Server className="h-4 w-4" />
            <span>Deployments</span>
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center space-x-2">
            <Zap className="h-4 w-4" />
            <span>Performance</span>
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center space-x-2">
            <Database className="h-4 w-4" />
            <span>Logs</span>
          </TabsTrigger>
          <TabsTrigger value="environment" className="flex items-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>Environment</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center space-x-2">
            <CreditCard className="h-4 w-4" />
            <span>Billing</span>
          </TabsTrigger>
        </TabsList>

        {/* Contenido de tabs */}
        <TabsContent value="overview" className="space-y-6">
          <AppHealthOverview
            healthData={healthData}
            deployments={deployments}
            performanceMetrics={performanceMetrics}
            supabaseHealth={supabaseHealth}
            isLoading={isLoading}
          />
        </TabsContent>

        <TabsContent value="deployments" className="space-y-6">
          <DeploymentHistory
            deployments={deployments}
            isLoading={isLoading}
            onRefresh={handleRefresh}
          />
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <PerformanceMetrics
            metrics={performanceMetrics}
            isLoading={isLoading}
            autoRefresh={autoRefresh}
          />
        </TabsContent>

        <TabsContent value="logs" className="space-y-6">
          <LogsMonitor
            logs={logs}
            isLoading={isLoading}
            autoRefresh={autoRefresh}
            onRefresh={handleRefresh}
          />
        </TabsContent>

        <TabsContent value="environment" className="space-y-6">
          <EnvironmentStatus
            status={environmentStatus}
            isLoading={isLoading}
            onRefresh={handleRefresh}
          />
        </TabsContent>

        <TabsContent value="billing" className="space-y-6">
          <BillingOverview
            billingData={billingData}
            supabaseHealth={supabaseHealth}
            isLoading={isBillingLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AppHealthPage;
